import React, { useState, useEffect, useCallback } from 'react';
import { transactionsAPI, invoicesAPI, statementAPI } from './api';
import { isIntrastate } from './gstUtils';

const fmt = (n, cur = 'INR') =>
  (cur === 'INR' ? '₹ ' : cur + ' ') +
  parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const getIndianFY = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  const m = d.getMonth() + 1;
  const y = d.getFullYear();
  return m >= 4 ? `${y}-${String(y + 1).slice(2)}` : `${y - 1}-${String(y).slice(2)}`;
};

const getQuarterKey = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  const m = d.getMonth() + 1;
  const y = d.getFullYear();
  const fy = m >= 4 ? `${y}-${String(y + 1).slice(2)}` : `${y - 1}-${String(y).slice(2)}`;
  const q = m >= 4 && m <= 6 ? 'Q1' : m >= 7 && m <= 9 ? 'Q2' : m >= 10 ? 'Q3' : 'Q4';
  const label = m >= 4 && m <= 6 ? 'Apr–Jun' : m >= 7 && m <= 9 ? 'Jul–Sep' : m >= 10 ? 'Oct–Dec' : 'Jan–Mar';
  const sortKey = `${fy}-${q}`;
  return { q, label, fy, sortKey, display: `${q} (${label}) FY ${fy}` };
};

const CAT_ICONS = {
  salary: '👤', rent: '🏢', utilities: '💡', travel: '✈️',
  supplies: '📦', tax: '🧾', software: '💻',
  invoice_payment: '💰', advance: '📥', other_income: '📈', other: '📋',
};

const DEBIT_CATS  = ['salary','rent','utilities','travel','supplies','tax','software','other'];
const CREDIT_CATS = ['invoice_payment','advance','other_income','other'];
const PAGE_SIZE   = 20;

/* ── Sort header ─────────────────────────────── */
function SortTh({ label, col, sortBy, sortDir, onSort, className }) {
  const active = sortBy === col;
  return (
    <th className={`sortable-th ${className || ''}${active ? ' sort-active' : ''}`}
        onClick={() => onSort(col)}>
      {label}
      <span className="sort-icon">
        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
      </span>
    </th>
  );
}

/* ── Add Entry Modal ─────────────────────────── */
function AddEntryModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    type: 'debit', amount: '', description: '',
    category: 'other', tx_date: new Date().toISOString().split('T')[0], currency: 'INR',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const cats = form.type === 'debit' ? DEBIT_CATS : CREDIT_CATS;

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try { await transactionsAPI.add(form); onSaved(); onClose(); }
    catch (er) { setErr(er.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box">
        <div className="modal-header">
          <h3 className="modal-title">Add Ledger Entry</h3>
          <div className="modal-type-toggle">
            <button type="button" className={form.type === 'debit'  ? 'active' : ''} onClick={() => { set('type','debit');  set('category','other'); }}>Debit</button>
            <button type="button" className={form.type === 'credit' ? 'active' : ''} onClick={() => { set('type','credit'); set('category','other'); }}>Credit</button>
          </div>
        </div>
        <form onSubmit={submit} className="modal-body">
          <div className="modal-grid2">
            <div className="modal-fg">
              <label className="modal-lbl">Amount</label>
              <input className="modal-inp" type="number" required step="0.01" min="0.01"
                value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
            </div>
            <div className="modal-fg">
              <label className="modal-lbl">Currency</label>
              <select className="modal-inp" value={form.currency} onChange={e => set('currency', e.target.value)}>
                {['INR','USD','EUR','GBP','AED','SGD'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="modal-fg">
              <label className="modal-lbl">Date</label>
              <input className="modal-inp" type="date" value={form.tx_date} onChange={e => set('tx_date', e.target.value)} />
            </div>
            <div className="modal-fg">
              <label className="modal-lbl">Category</label>
              <select className="modal-inp" value={form.category} onChange={e => set('category', e.target.value)}>
                {cats.map(c => <option key={c} value={c}>{c.replace(/_/g,' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="modal-fg">
            <label className="modal-lbl">Description *</label>
            <input className="modal-inp" required value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="e.g. Office rent June 2026" />
          </div>
          {err && <div className="modal-error">{err}</div>}
          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving}>
              {saving ? 'Saving…' : `Add ${form.type === 'debit' ? 'Debit' : 'Credit'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Opening Balance Warning ─────────────────── */
function OpeningBalanceWarning({ opening, onZeroed }) {
  const [zeroing, setZeroing] = useState(false);
  const [done,    setDone]    = useState(false);

  if (done) return null;

  const handleZero = async () => {
    if (!window.confirm(`This will remove the ₹${parseFloat(opening).toLocaleString('en-IN', { minimumFractionDigits: 2 })} opening balance from your ledger balance calculation. Your actual transaction entries are not affected. Proceed?`)) return;
    setZeroing(true);
    try {
      await transactionsAPI.setOpeningBal(0);
      setDone(true);
      onZeroed();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setZeroing(false);
    }
  };

  return (
    <div className="ob-warning">
      <span className="ob-icon">⚠️</span>
      <span className="ob-text">
        Your ledger has an <strong>opening balance of {fmt(opening)}</strong> that was added on top of all
        your recorded transactions. Since you have entered all data from FY 2022-23, this is likely inflating
        your ledger balance by that amount.
      </span>
      <button className="ob-zero-btn" onClick={handleZero} disabled={zeroing}>
        {zeroing ? 'Removing…' : 'Remove opening balance'}
      </button>
    </div>
  );
}

/* ── Bulk Delete Modal ───────────────────────── */
function BulkDeleteModal({ onClose, onDeleted }) {
  const [from,     setFrom]     = useState('2026-04-01');
  const [to,       setTo]       = useState('2026-08-31');
  const [txType,   setTxType]   = useState('debit');   // 'debit' | 'credit' | 'all'
  const [preview,  setPreview]  = useState(null);      // { count, total }
  const [loading,  setLoading]  = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm,  setConfirm]  = useState(false);
  const [error,    setError]    = useState('');

  const fetchPreview = async () => {
    setLoading(true); setError(''); setPreview(null); setConfirm(false);
    try {
      const params = new URLSearchParams({ from, to, type: txType });
      const res = await fetch(`/api/transactions/bulk-preview?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ti_token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      setPreview(data);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    setDeleting(true); setError('');
    try {
      const params = new URLSearchParams({ from, to, type: txType });
      const res = await fetch(`/api/transactions/bulk-delete?${params}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('ti_token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      onDeleted(data.deleted);
      onClose();
    } catch(e) { setError(e.message); }
    finally { setDeleting(false); }
  };

  const fmtAmt = n => '₹' + Number(n||0).toLocaleString('en-IN', {minimumFractionDigits:2});

  return (
    <div className="modal-overlay" onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div className="modal-box" style={{maxWidth:480}}>
        <div className="modal-header">
          <h3 className="modal-title">Bulk Delete Entries</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:16}}>

          <div className="modal-grid2">
            <div className="modal-fg">
              <label className="modal-lbl">From date</label>
              <input className="modal-inp" type="date" value={from} onChange={e => { setFrom(e.target.value); setPreview(null); }} />
            </div>
            <div className="modal-fg">
              <label className="modal-lbl">To date</label>
              <input className="modal-inp" type="date" value={to} onChange={e => { setTo(e.target.value); setPreview(null); }} />
            </div>
          </div>

          <div className="modal-fg">
            <label className="modal-lbl">Entry type to delete</label>
            <div style={{display:'flex',gap:8}}>
              {[['debit','− Debits only'],['credit','+ Credits only'],['all','Both debits & credits']].map(([v,l]) => (
                <button key={v}
                  style={{flex:1,padding:'7px 0',border:'1.5px solid',borderRadius:8,fontSize:12.5,fontWeight:600,cursor:'pointer',
                    borderColor: txType===v ? '#dc2626' : '#e2e8f0',
                    background:  txType===v ? '#fef2f2' : '#fff',
                    color:       txType===v ? '#dc2626' : '#475569',
                  }}
                  onClick={() => { setTxType(v); setPreview(null); }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <button className="ledger-add-btn" onClick={fetchPreview} disabled={loading}
            style={{alignSelf:'flex-start',background:'#1e5fa8',color:'#fff',border:'none'}}>
            {loading ? 'Checking…' : 'Preview what will be deleted'}
          </button>

          {preview && (
            <div style={{background:'#fef2f2',border:'1.5px solid #fecaca',borderRadius:10,padding:'14px 18px'}}>
              <div style={{fontWeight:700,fontSize:15,color:'#991b1b',marginBottom:6}}>
                ⚠️ {preview.count} {txType === 'all' ? '' : txType} entr{preview.count!==1?'ies':'y'} will be permanently deleted
              </div>
              <div style={{fontSize:13,color:'#7f1d1d'}}>
                Total amount: <strong>{fmtAmt(preview.totalAmount)}</strong><br/>
                Date range: <strong>{from}</strong> to <strong>{to}</strong><br/>
                {preview.invPayCount > 0 && (
                  <span style={{color:'#b45309'}}>
                    ⚡ Includes {preview.invPayCount} auto-managed invoice payment entries
                  </span>
                )}
              </div>
              {!confirm && (
                <button onClick={() => setConfirm(true)}
                  style={{marginTop:12,padding:'7px 18px',background:'#dc2626',color:'#fff',border:'none',
                    borderRadius:7,fontSize:13,fontWeight:600,cursor:'pointer'}}>
                  Yes, I want to delete these
                </button>
              )}
              {confirm && (
                <div style={{marginTop:12,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <span style={{fontSize:12.5,color:'#991b1b',fontWeight:600}}>Are you absolutely sure? This cannot be undone.</span>
                  <button onClick={handleDelete} disabled={deleting}
                    style={{padding:'7px 20px',background:'#991b1b',color:'#fff',border:'none',
                      borderRadius:7,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                    {deleting ? 'Deleting…' : `Delete ${preview.count} entries permanently`}
                  </button>
                  <button onClick={() => setConfirm(false)}
                    style={{padding:'7px 14px',background:'#fff',border:'1px solid #e2e8f0',
                      borderRadius:7,fontSize:13,cursor:'pointer',color:'#475569'}}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          {error && <div className="bi-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── Bank Statement Import Modal ─────────────── */
function BankImportModal({ onClose, onSaved }) {
  const [step,      setStep]      = useState('upload'); // 'upload' | 'review'
  const [newRows,   setNewRows]   = useState([]);       // unmatched bank rows only
  const [matchedCt, setMatchedCt] = useState(0);
  const [summary,   setSummary]   = useState(null);
  const [cats,      setCats]      = useState({});
  const [skip,      setSkip]      = useState(new Set()); // rows user has unchecked
  const [importing, setImporting] = useState(false);
  const [importDone,setImportDone]= useState(null);     // { ok, fail }
  const [parsing,   setParsing]   = useState(false);
  const [error,     setError]     = useState('');
  const [showAll,   setShowAll]   = useState(false);    // toggle to see matched rows too
  const [allRows,   setAllRows]   = useState([]);

  const inferCat = (desc = '', type = 'debit') => {
    const d = desc.toLowerCase();
    if (type === 'credit') {
      if (/payment|receipt|paid/.test(d)) return 'invoice_payment';
      if (/advance/.test(d)) return 'advance';
      return 'other_income';
    }
    if (/salary|payroll|wages/.test(d)) return 'salary';
    if (/rent|lease/.test(d)) return 'rent';
    if (/utility|electric|water|internet|broadband/.test(d)) return 'utilities';
    if (/travel|hotel|taxi|cab|uber|ola|train|flight/.test(d)) return 'travel';
    if (/supplies|stationery|print/.test(d)) return 'supplies';
    if (/tax|gst|tds|income/.test(d)) return 'tax';
    if (/software|saas|aws|azure|cloud|google/.test(d)) return 'software';
    return 'other';
  };

  const fmtAmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', {minimumFractionDigits: 2});

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setError('Please upload an .xlsx, .xls, or .csv file.'); return;
    }
    setError(''); setParsing(true);
    try {
      const result = await statementAPI.reconcile(file);
      const annotated = (result.rows || []).map((r, idx) => ({
        ...r, idx,
        category: r.matchedCat || inferCat(r.desc || '', r.type || 'debit'),
      }));
      const unmatched = annotated.filter(r => r.status === 'unmatched');
      setAllRows(annotated);
      setNewRows(unmatched);
      setMatchedCt(annotated.filter(r => r.status === 'matched').length);
      setSummary(result.summary || {});
      setCats(Object.fromEntries(annotated.map(r => [r.idx, r.category])));
      setSkip(new Set());
      setImportDone(null);
      setStep('review');
    } catch (err) {
      setError(err.message || 'Failed to process file.');
    } finally {
      setParsing(false);
    }
  };

  const toImport = newRows.filter(r => !skip.has(r.idx));

  const handleImport = async () => {
    if (!toImport.length) return;
    setImporting(true); setError('');
    let ok = 0, fail = 0;
    for (const row of toImport) {
      try {
        await transactionsAPI.add({
          type: row.type, amount: row.amount,
          description: row.desc || '(bank import)',
          category: cats[row.idx] || 'other',
          tx_date: row.date, currency: 'INR',
        });
        ok++;
      } catch { fail++; }
    }
    setImporting(false);
    if (fail > 0) {
      setError(`${ok} imported, ${fail} failed.`);
    } else {
      setImportDone({ ok });
      onSaved(); // refresh ledger in background
    }
  };

  const displayRows = showAll ? allRows : newRows;

  return (
    <div className="modal-overlay bi-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box bi-box">
        <div className="modal-header">
          <h3 className="modal-title">Import Bank Statement</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* ── Upload ── */}
        {step === 'upload' && (
          <div className="bi-upload-area">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <p className="bi-upload-title">Upload your bank statement</p>
            <p className="bi-upload-hint">
              Supports <strong>ICICI, SBI, HDFC, Axis, Kotak</strong> — <strong>.xlsx</strong> or <strong>.csv</strong><br/>
              We will automatically check which transactions are already in your ledger.
            </p>
            <label className="bi-file-label" style={parsing ? {opacity:0.6,pointerEvents:'none'} : {}}>
              {parsing
                ? <><span className="bi-spinner"/>Checking against ledger…</>
                : 'Choose File (.xlsx or .csv)'}
              <input type="file" accept=".xlsx,.xls,.csv" hidden disabled={parsing}
                onChange={e => handleFile(e.target.files?.[0])} />
            </label>
            {error && <div className="bi-error">{error}</div>}
          </div>
        )}

        {/* ── Review & Import ── */}
        {step === 'review' && summary && (
          <>
            {/* Import done banner */}
            {importDone && (
              <div className="bi-done-banner">
                ✅ {importDone.ok} transactions imported successfully!
                <button className="bi-done-close" onClick={onClose}>Close</button>
              </div>
            )}

            {/* Summary strip */}
            <div className="bi-strip">
              <div className="bi-strip-item">
                <strong>{summary.total}</strong>
                <span>in statement</span>
                {summary.dateRange?.start && (
                  <span className="bi-strip-range">{summary.dateRange.start} → {summary.dateRange.end}</span>
                )}
              </div>
              <div className="bi-strip-sep">·</div>
              <div className="bi-strip-item bi-strip-matched">
                <strong>{matchedCt}</strong>
                <span>already in ledger</span>
              </div>
              <div className="bi-strip-sep">·</div>
              <div className="bi-strip-item bi-strip-new">
                <strong>{newRows.length}</strong>
                <span>new to import</span>
              </div>
              <div className="bi-strip-sep">·</div>
              <div className="bi-strip-item">
                <strong style={{color:'#059669'}}>{fmtAmt(summary.totalCredit)}</strong>
                <span>credits</span>
              </div>
              <div className="bi-strip-sep">·</div>
              <div className="bi-strip-item">
                <strong style={{color:'#dc2626'}}>{fmtAmt(summary.totalDebit)}</strong>
                <span>debits</span>
              </div>
            </div>

            {/* All-matched happy path */}
            {newRows.length === 0 && (
              <div className="bi-all-matched">
                🎉 All {summary.total} bank transactions are already recorded in your ledger. Nothing to import!
              </div>
            )}

            {/* New transactions table */}
            {newRows.length > 0 && !importDone && (
              <>
                <div className="bi-table-header-bar">
                  <span>
                    {showAll
                      ? `All ${allRows.length} transactions (${matchedCt} already recorded shown in green)`
                      : `${newRows.length} new transactions to import — uncheck any you want to skip`}
                  </span>
                  <button className="bi-toggle-link" onClick={() => setShowAll(v => !v)}>
                    {showAll ? 'Hide already-recorded' : `Show already-recorded (${matchedCt})`}
                  </button>
                </div>

                <div className="bi-table-wrap">
                  <table className="bi-table">
                    <thead>
                      <tr>
                        <th style={{width:28}}/>
                        <th>Date</th>
                        <th>Bank Description</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map(row => {
                        const isMatched = row.status === 'matched';
                        const isSkipped = skip.has(row.idx);
                        const rowCats   = row.type === 'debit' ? DEBIT_CATS : CREDIT_CATS;
                        return (
                          <tr key={row.idx}
                            className={`${isMatched ? 'bi-row-matched' : ''}${isSkipped ? ' bi-row-dim' : ''}`}>
                            <td>
                              {isMatched
                                ? <span title="Already in ledger" style={{fontSize:14}}>✅</span>
                                : (
                                  <input type="checkbox" checked={!isSkipped}
                                    onChange={e => {
                                      const s = new Set(skip);
                                      e.target.checked ? s.delete(row.idx) : s.add(row.idx);
                                      setSkip(s);
                                    }} />
                                )
                              }
                            </td>
                            <td className="bi-td-date">{row.date || '—'}</td>
                            <td className="bi-td-desc" title={row.desc}>
                              {row.desc || '—'}
                              {isMatched && row.matchedDesc && (
                                <div className="bi-matched-hint">↳ {row.matchedDesc}</div>
                              )}
                            </td>
                            <td>
                              <span className={`ledger-type-badge ${row.type}`}>
                                {row.type === 'credit' ? '+ Credit' : '− Debit'}
                              </span>
                            </td>
                            <td className={`bi-td-amt ${row.type}`}>
                              {row.type === 'credit' ? '+' : '−'} {fmtAmt(row.amount)}
                            </td>
                            <td>
                              {isMatched
                                ? <span className="bi-cat-locked">{CAT_ICONS[cats[row.idx]] || '📋'} {(cats[row.idx]||'other').replace(/_/g,' ')}</span>
                                : (
                                  <select className="bi-cat-sel" value={cats[row.idx] || 'other'}
                                    onChange={e => setCats(c => ({...c, [row.idx]: e.target.value}))}>
                                    {rowCats.map(c => (
                                      <option key={c} value={c}>{CAT_ICONS[c] || '📋'} {c.replace(/_/g,' ')}</option>
                                    ))}
                                  </select>
                                )
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {error && <div className="bi-error" style={{padding:'8px 16px'}}>{error}</div>}
          </>
        )}

        <div className="modal-footer">
          <button className="modal-cancel"
            onClick={step === 'review' && !importDone
              ? () => { setStep('upload'); setError(''); }
              : onClose}>
            {step === 'review' && !importDone ? '← Upload Different File' : 'Close'}
          </button>
          {step === 'review' && !importDone && newRows.length > 0 && (
            <button className="modal-save" onClick={handleImport}
              disabled={importing || toImport.length === 0}>
              {importing
                ? `Importing ${toImport.length}…`
                : `Import ${toImport.length} Transaction${toImport.length !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── GST Tab ─────────────────────────────────── */
function GSTTab({ firm }) {
  const [invoices,  setInvoices]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [fyFilter,  setFyFilter]  = useState('all');
  const [sortBy,    setSortBy]    = useState('invoice_date');
  const [sortDir,   setSortDir]   = useState('desc');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setInvoices(await invoicesAPI.list()); }
      catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const onSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  /* Only invoices with GST */
  const gstInvoices = invoices.filter(i => parseFloat(i.gst_amount || i.gstAmount || 0) > 0);

  /* Augment each invoice with intrastate flag & computed amounts */
  const enriched = gstInvoices.map(i => {
    const gstAmt    = parseFloat(i.gst_amount || i.gstAmount || 0);
    const intra     = isIntrastate(firm?.gstNumber, i.place_of_supply || i.placeOfSupply);
    const cgst      = intra ? gstAmt / 2 : 0;
    const sgst      = intra ? gstAmt / 2 : 0;
    const igst      = intra ? 0 : gstAmt;
    const dateStr   = i.invoice_date || i.invoiceDate || '';
    const paidDate  = i.payment_date || i.paymentDate || '';
    const fy        = getIndianFY(dateStr);
    const qObj      = getQuarterKey(dateStr);
    return { ...i, gstAmt, intra, cgst, sgst, igst, fy, qObj, dateStr, paidDate };
  });

  /* FY options */
  const fyOptions = [...new Set(enriched.map(i => i.fy))].filter(f => f !== 'Unknown').sort().reverse();

  const byFY = fyFilter === 'all' ? enriched : enriched.filter(i => i.fy === fyFilter);

  /* Sort */
  const sorted = [...byFY].sort((a, b) => {
    let av, bv;
    if (sortBy === 'invoice_date') { av = a.dateStr; bv = b.dateStr; }
    else if (sortBy === 'paid_on')  { av = a.paidDate; bv = b.paidDate; }
    else if (sortBy === 'gstAmt')   { return sortDir === 'asc' ? a.gstAmt - b.gstAmt : b.gstAmt - a.gstAmt; }
    else { av = a.dateStr; bv = b.dateStr; }
    if (!av && !bv) return 0;
    if (!av) return 1; if (!bv) return -1;
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  /* Summary tiles */
  const totalGST  = byFY.reduce((s, i) => s + i.gstAmt, 0);
  const totalCGST = byFY.reduce((s, i) => s + i.cgst, 0);
  const totalSGST = byFY.reduce((s, i) => s + i.sgst, 0);
  const totalIGST = byFY.reduce((s, i) => s + i.igst, 0);
  const paidGST   = byFY.filter(i => (i.payment_status || i.paymentStatus) === 'paid').reduce((s, i) => s + i.gstAmt, 0);
  const pendGST   = totalGST - paidGST;

  /* Quarter breakdown */
  const quarterMap = {};
  byFY.forEach(i => {
    const k = i.qObj.sortKey;
    if (!quarterMap[k]) quarterMap[k] = { ...i.qObj, count: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };
    const q = quarterMap[k];
    q.count++;
    q.taxable += parseFloat(i.subtotal || 0);
    q.cgst += i.cgst; q.sgst += i.sgst; q.igst += i.igst; q.total += i.gstAmt;
  });
  const quarters = Object.values(quarterMap).sort((a, b) => b.sortKey.localeCompare(a.sortKey));

  const statusBadge = (s) => {
    const st = s || 'unpaid';
    return <span className={`inv-status iv-status-${st}`}>{st === 'paid' ? 'Paid' : st === 'partial' ? 'Partial' : 'Pending'}</span>;
  };

  if (loading) return <div className="ledger-loading">Loading invoices…</div>;

  return (
    <div className="tax-tab-root">
      {/* FY filter */}
      <div className="tax-fy-row">
        <label className="tax-fy-lbl">Financial Year</label>
        <div className="ledger-type-tabs">
          <button className={`ledger-type-tab${fyFilter==='all'?' active':''}`} onClick={() => setFyFilter('all')}>All FYs</button>
          {fyOptions.map(fy => (
            <button key={fy} className={`ledger-type-tab${fyFilter===fy?' active':''}`} onClick={() => setFyFilter(fy)}>{fy}</button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="tax-tiles">
        <div className="tax-tile tax-tile-total">
          <div className="tax-tile-label">Total Output GST</div>
          <div className="tax-tile-value">{fmt(totalGST)}</div>
          <div className="tax-tile-sub">{byFY.length} invoice{byFY.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="tax-tile tax-tile-cgst">
          <div className="tax-tile-label">CGST (Intrastate)</div>
          <div className="tax-tile-value">{fmt(totalCGST)}</div>
          <div className="tax-tile-sub">payable to Centre</div>
        </div>
        <div className="tax-tile tax-tile-sgst">
          <div className="tax-tile-label">SGST (Intrastate)</div>
          <div className="tax-tile-value">{fmt(totalSGST)}</div>
          <div className="tax-tile-sub">payable to State</div>
        </div>
        <div className="tax-tile tax-tile-igst">
          <div className="tax-tile-label">IGST (Interstate)</div>
          <div className="tax-tile-value">{fmt(totalIGST)}</div>
          <div className="tax-tile-sub">payable to Centre</div>
        </div>
        <div className="tax-tile tax-tile-paid">
          <div className="tax-tile-label">From Paid Invoices</div>
          <div className="tax-tile-value">{fmt(paidGST)}</div>
          <div className="tax-tile-sub">received from clients</div>
        </div>
        <div className="tax-tile tax-tile-pend">
          <div className="tax-tile-label">From Unpaid Invoices</div>
          <div className="tax-tile-value">{fmt(pendGST)}</div>
          <div className="tax-tile-sub">GST liability on accrual</div>
        </div>
      </div>

      {/* Quarterly breakdown */}
      {quarters.length > 0 && (
        <div className="tax-section">
          <div className="tax-section-title">Quarter-wise Summary</div>
          <div className="ledger-table-wrap">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Quarter</th>
                  <th className="th-right">Invoices</th>
                  <th className="th-right">Taxable Amt</th>
                  <th className="th-right">CGST</th>
                  <th className="th-right">SGST</th>
                  <th className="th-right">IGST</th>
                  <th className="th-right">Total GST</th>
                </tr>
              </thead>
              <tbody>
                {quarters.map(q => (
                  <tr key={q.sortKey} className="iv-row">
                    <td><strong>{q.display}</strong></td>
                    <td className="td-amount">{q.count}</td>
                    <td className="td-amount">{fmt(q.taxable)}</td>
                    <td className="td-amount td-cgst">{q.cgst > 0 ? fmt(q.cgst) : '—'}</td>
                    <td className="td-amount td-sgst">{q.sgst > 0 ? fmt(q.sgst) : '—'}</td>
                    <td className="td-amount td-igst">{q.igst > 0 ? fmt(q.igst) : '—'}</td>
                    <td className="td-amount credit"><strong>{fmt(q.total)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice-level detail */}
      <div className="tax-section">
        <div className="tax-section-title">Invoice-wise Detail</div>
        {sorted.length === 0 ? (
          <div className="ledger-empty"><p>No GST invoices{fyFilter !== 'all' ? ` for FY ${fyFilter}` : ''}.</p></div>
        ) : (
          <div className="ledger-table-wrap">
            <table className="ledger-table">
              <thead>
                <tr>
                  <SortTh label="Invoice #"    col="invoice_number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="Invoice Date" col="invoice_date"   sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                  <th>Client</th>
                  <th>Rate</th>
                  <th className="th-right">Taxable</th>
                  <th className="th-right">CGST</th>
                  <th className="th-right">SGST</th>
                  <th className="th-right">IGST</th>
                  <th className="th-right">GST Total</th>
                  <SortTh label="Paid On"  col="paid_on"  sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="th-right" />
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(i => (
                  <tr key={i.id || i.invoice_number} className="iv-row">
                    <td className="td-ref"><span className="ledger-ref">#{i.invoice_number || i.invoiceNumber}</span></td>
                    <td className="td-date">{fmtDate(i.dateStr)}</td>
                    <td className="td-desc">{i.client_name || i.clientName || '—'}</td>
                    <td><span className={`tax-rate-badge ${i.intra ? 'intra' : 'inter'}`}>{i.intra ? `${parseFloat(i.gst_rate||i.gstRate||0)/2}%+${parseFloat(i.gst_rate||i.gstRate||0)/2}%` : `${i.gst_rate||i.gstRate||0}%`}</span></td>
                    <td className="td-amount">{fmt(i.subtotal || 0)}</td>
                    <td className="td-amount td-cgst">{i.cgst > 0 ? fmt(i.cgst) : '—'}</td>
                    <td className="td-amount td-sgst">{i.sgst > 0 ? fmt(i.sgst) : '—'}</td>
                    <td className="td-amount td-igst">{i.igst > 0 ? fmt(i.igst) : '—'}</td>
                    <td className="td-amount credit"><strong>{fmt(i.gstAmt)}</strong></td>
                    <td className="td-date">{fmtDate(i.paidDate)}</td>
                    <td>{statusBadge(i.payment_status || i.paymentStatus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {byFY.length > 0 && (
        <div className="tax-note">
          ⚠️ GST liability arises on the invoice date under accrual basis. CGST &amp; SGST apply for intrastate supply (same state as firm GSTIN); IGST applies for interstate or export supply.
        </div>
      )}
    </div>
  );
}

/* ── TDS Tab ─────────────────────────────────── */
function TDSTab() {
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [fyFilter, setFyFilter] = useState('all');
  const [sortBy,   setSortBy]   = useState('payment_date');
  const [sortDir,  setSortDir]  = useState('desc');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setInvoices(await invoicesAPI.list()); }
      catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const onSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  /* Only invoices with TDS */
  const tdsInvoices = invoices.filter(i => parseFloat(i.tds_amount || i.tdsAmount || 0) > 0);

  const enriched = tdsInvoices.map(i => {
    const tdsAmt  = parseFloat(i.tds_amount || i.tdsAmount || 0);
    const tdsRate = parseFloat(i.tds_rate  || i.tdsRate  || 0);
    const dateStr  = i.invoice_date  || i.invoiceDate  || '';
    const paidDate = i.payment_date  || i.paymentDate  || '';
    const fy = getIndianFY(paidDate || dateStr);
    const qObj = getQuarterKey(paidDate || dateStr);
    return { ...i, tdsAmt, tdsRate, dateStr, paidDate, fy, qObj };
  });

  const fyOptions = [...new Set(enriched.map(i => i.fy))].filter(f => f !== 'Unknown').sort().reverse();

  const byFY = fyFilter === 'all' ? enriched : enriched.filter(i => i.fy === fyFilter);

  /* Sort */
  const sorted = [...byFY].sort((a, b) => {
    if (sortBy === 'tdsAmt') return sortDir === 'asc' ? a.tdsAmt - b.tdsAmt : b.tdsAmt - a.tdsAmt;
    const av = sortBy === 'payment_date' ? a.paidDate : a.dateStr;
    const bv = sortBy === 'payment_date' ? b.paidDate : b.dateStr;
    if (!av && !bv) return 0;
    if (!av) return 1; if (!bv) return -1;
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  /* Summary */
  const totalTDS   = byFY.reduce((s, i) => s + i.tdsAmt, 0);
  const curFY      = getIndianFY(new Date().toISOString());
  const thisFYTDS  = enriched.filter(i => i.fy === curFY).reduce((s, i) => s + i.tdsAmt, 0);
  const paidTDS    = byFY.filter(i => (i.payment_status||i.paymentStatus) === 'paid').reduce((s, i) => s + i.tdsAmt, 0);

  /* Quarter breakdown */
  const quarterMap = {};
  byFY.forEach(i => {
    const k = i.qObj.sortKey;
    if (!quarterMap[k]) quarterMap[k] = { ...i.qObj, count: 0, invoiceTotal: 0, tds: 0 };
    quarterMap[k].count++;
    quarterMap[k].invoiceTotal += parseFloat(i.total || 0);
    quarterMap[k].tds += i.tdsAmt;
  });
  const quarters = Object.values(quarterMap).sort((a, b) => b.sortKey.localeCompare(a.sortKey));

  const statusBadge = (s) => {
    const st = s || 'unpaid';
    return <span className={`inv-status iv-status-${st}`}>{st === 'paid' ? 'Paid' : st === 'partial' ? 'Partial' : 'Pending'}</span>;
  };

  if (loading) return <div className="ledger-loading">Loading invoices…</div>;

  return (
    <div className="tax-tab-root">
      {/* FY filter */}
      <div className="tax-fy-row">
        <label className="tax-fy-lbl">Financial Year</label>
        <div className="ledger-type-tabs">
          <button className={`ledger-type-tab${fyFilter==='all'?' active':''}`} onClick={() => setFyFilter('all')}>All FYs</button>
          {fyOptions.map(fy => (
            <button key={fy} className={`ledger-type-tab${fyFilter===fy?' active':''}`} onClick={() => setFyFilter(fy)}>{fy}</button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="tax-tiles">
        <div className="tax-tile tax-tile-total">
          <div className="tax-tile-label">Total TDS Deducted</div>
          <div className="tax-tile-value">{fmt(totalTDS)}</div>
          <div className="tax-tile-sub">{byFY.length} invoice{byFY.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="tax-tile tax-tile-cgst">
          <div className="tax-tile-label">This FY ({curFY})</div>
          <div className="tax-tile-value">{fmt(thisFYTDS)}</div>
          <div className="tax-tile-sub">current financial year</div>
        </div>
        <div className="tax-tile tax-tile-paid">
          <div className="tax-tile-label">On Paid Invoices</div>
          <div className="tax-tile-value">{fmt(paidTDS)}</div>
          <div className="tax-tile-sub">TDS already deposited by clients</div>
        </div>
        <div className="tax-tile tax-tile-pend">
          <div className="tax-tile-label">On Unpaid Invoices</div>
          <div className="tax-tile-value">{fmt(totalTDS - paidTDS)}</div>
          <div className="tax-tile-sub">to be deducted at payment</div>
        </div>
      </div>

      {/* Quarter breakdown */}
      {quarters.length > 0 && (
        <div className="tax-section">
          <div className="tax-section-title">Quarter-wise TDS Summary <span className="tax-section-note">(grouped by payment date)</span></div>
          <div className="ledger-table-wrap">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Quarter</th>
                  <th className="th-right">Invoices</th>
                  <th className="th-right">Invoice Value</th>
                  <th className="th-right">TDS Deducted</th>
                  <th className="th-right">Effective TDS %</th>
                </tr>
              </thead>
              <tbody>
                {quarters.map(q => (
                  <tr key={q.sortKey} className="iv-row">
                    <td><strong>{q.display}</strong></td>
                    <td className="td-amount">{q.count}</td>
                    <td className="td-amount">{fmt(q.invoiceTotal)}</td>
                    <td className="td-amount credit"><strong>{fmt(q.tds)}</strong></td>
                    <td className="td-amount">
                      {q.invoiceTotal > 0 ? `${((q.tds / q.invoiceTotal) * 100).toFixed(2)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice-level detail */}
      <div className="tax-section">
        <div className="tax-section-title">Invoice-wise TDS Detail</div>
        {sorted.length === 0 ? (
          <div className="ledger-empty"><p>No invoices with TDS{fyFilter !== 'all' ? ` for FY ${fyFilter}` : ''}.</p></div>
        ) : (
          <div className="ledger-table-wrap">
            <table className="ledger-table">
              <thead>
                <tr>
                  <SortTh label="Invoice #"    col="invoice_number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                  <SortTh label="Invoice Date" col="invoice_date"   sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                  <th>Client</th>
                  <th className="th-right">Invoice Total</th>
                  <th className="th-right">TDS Rate</th>
                  <th className="th-right">TDS Amount</th>
                  <SortTh label="Paid On" col="payment_date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="th-right" />
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(i => (
                  <tr key={i.id || i.invoice_number} className="iv-row">
                    <td className="td-ref"><span className="ledger-ref">#{i.invoice_number || i.invoiceNumber}</span></td>
                    <td className="td-date">{fmtDate(i.dateStr)}</td>
                    <td className="td-desc">{i.client_name || i.clientName || '—'}</td>
                    <td className="td-amount">{fmt(i.total || 0)}</td>
                    <td className="td-amount">
                      {i.tdsRate > 0 ? `${i.tdsRate.toFixed(2)}%` : <span className="tds-computed">computed</span>}
                    </td>
                    <td className="td-amount credit"><strong>{fmt(i.tdsAmt)}</strong></td>
                    <td className="td-date">{fmtDate(i.paidDate)}</td>
                    <td>{statusBadge(i.payment_status || i.paymentStatus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {byFY.length > 0 && (
        <div className="tax-note">
          ℹ️ TDS is deducted by clients at source under Section 194J (professional services, typically 10%) or 194C. Use Form 26AS to reconcile and claim credit when filing your income tax return.
        </div>
      )}
    </div>
  );
}

/* ── Edit Entry Modal ────────────────────────── */
function EditEntryModal({ tx, onClose, onSaved }) {
  const isAutoManaged = tx.category === 'invoice_payment';
  const cats = tx.type === 'debit' ? DEBIT_CATS : CREDIT_CATS;

  const [form, setForm] = useState({
    description: tx.description || '',
    reference:   tx.reference   || '',
    category:    tx.category    || 'other',
    tx_date:     tx.tx_date ? String(tx.tx_date).split('T')[0] : '',
    amount:      String(parseFloat(tx.amount || 0)),
    type:        tx.type || 'debit',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      const payload = isAutoManaged
        ? { description: form.description }
        : { description: form.description, reference: form.reference,
            category: form.category, tx_date: form.tx_date,
            amount: form.amount, type: form.type };
      await transactionsAPI.update(tx.id, payload);
      onSaved();
      onClose();
    } catch (er) {
      setErr(er.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box">
        <div className="modal-header">
          <h3 className="modal-title">Edit Ledger Entry</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit} className="modal-body">
          {isAutoManaged && (
            <div className="edit-auto-notice">
              ℹ️ This is an auto-managed invoice payment. Only the description can be edited; amount, date and category are controlled by the invoice.
            </div>
          )}
          <div className="modal-grid2">
            {!isAutoManaged && (
              <>
                <div className="modal-fg">
                  <label className="modal-lbl">Amount</label>
                  <input className="modal-inp" type="number" required step="0.01" min="0.01"
                    value={form.amount} onChange={e => set('amount', e.target.value)} />
                </div>
                <div className="modal-fg">
                  <label className="modal-lbl">Date</label>
                  <input className="modal-inp" type="date" required value={form.tx_date}
                    onChange={e => set('tx_date', e.target.value)} />
                </div>
                <div className="modal-fg">
                  <label className="modal-lbl">Type</label>
                  <div className="modal-type-toggle">
                    <button type="button" className={form.type === 'debit'  ? 'active' : ''} onClick={() => { set('type','debit');  set('category','other'); }}>Debit</button>
                    <button type="button" className={form.type === 'credit' ? 'active' : ''} onClick={() => { set('type','credit'); set('category','other_income'); }}>Credit</button>
                  </div>
                </div>
                <div className="modal-fg">
                  <label className="modal-lbl">Category</label>
                  <select className="modal-inp" value={form.category} onChange={e => set('category', e.target.value)}>
                    {cats.map(c => <option key={c} value={c}>{c.replace(/_/g,' ')}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
          <div className="modal-fg">
            <label className="modal-lbl">Description</label>
            <input className="modal-inp" value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="e.g. Office rent June 2026" />
          </div>
          {!isAutoManaged && (
            <div className="modal-fg">
              <label className="modal-lbl">Reference / Notes</label>
              <input className="modal-inp" value={form.reference}
                onChange={e => set('reference', e.target.value)}
                placeholder="e.g. Cheque no., invoice no., …" />
            </div>
          )}
          {err && <div className="modal-error">{err}</div>}
          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main Ledger Page ────────────────────────── */
export default function Ledger({ firm }) {
  const [activeTab,    setActiveTab]    = useState('bank');
  const [transactions, setTransactions] = useState([]);
  const [balance,      setBalance]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showModal,      setShowModal]      = useState(false);
  const [showImport,     setShowImport]     = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [editEntry,      setEditEntry]      = useState(null);   // tx to edit

  /* Filters */
  const [typeFilter, setTypeFilter] = useState('all');
  const [catFilter,  setCatFilter]  = useState('all');
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);

  /* Sort */
  const [sortBy,  setSortBy]  = useState('tx_date');
  const [sortDir, setSortDir] = useState('desc');

  const onSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (typeFilter !== 'all') params.type = typeFilter;
      const [txs, bal] = await Promise.all([transactionsAPI.list(params), transactionsAPI.balance()]);
      setTransactions(txs);
      setBalance(bal);
    } catch {}
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { load(); setPage(1); }, [load]);

  /* All categories from data */
  const allCats = [...new Set(transactions.map(t => t.category).filter(Boolean))].sort();

  /* Apply filters + search + sort */
  const filtered = transactions.filter(t => {
    if (catFilter !== 'all' && t.category !== catFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.description || '').toLowerCase().includes(q) ||
           (t.invoice_number || '').toLowerCase().includes(q) ||
           (t.category || '').includes(q);
  });

  const sortedFiltered = [...filtered].sort((a, b) => {
    if (sortBy === 'amount') {
      return sortDir === 'asc'
        ? parseFloat(a.amount) - parseFloat(b.amount)
        : parseFloat(b.amount) - parseFloat(a.amount);
    }
    /* tx_date or paid_on — both use same field here (tx_date IS the payment date for invoice_payment) */
    const av = a.tx_date || '';
    const bv = b.tx_date || '';
    if (!av && !bv) return 0;
    if (!av) return 1; if (!bv) return -1;
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = sortedFiltered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const totalCredit = filtered.filter(t => t.type === 'credit').reduce((s, t) => s + parseFloat(t.amount), 0);
  const totalDebit  = filtered.filter(t => t.type === 'debit').reduce((s, t) => s + parseFloat(t.amount), 0);

  const handleDelete = async (id, cat) => {
    if (cat === 'invoice_payment') {
      alert('Invoice payment entries are auto-managed. Change the invoice payment status to remove them.');
      return;
    }
    if (!window.confirm('Delete this entry?')) return;
    try { await transactionsAPI.remove(id); load(); } catch (err) { alert(err.message); }
  };

  const TAB_DEFS = [
    { key: 'bank', label: '🏦 Bank Ledger' },
    { key: 'gst',  label: '🧾 GST' },
    { key: 'tds',  label: '📋 TDS' },
  ];

  return (
    <div className="ledger-page">
      {/* Header */}
      <div className="ledger-topbar">
        <div>
          <h1 className="ledger-title">Ledger</h1>
          <p className="ledger-sub">Bank transactions · GST output · TDS deductions</p>
        </div>
        {activeTab === 'bank' && (
          <div style={{display:'flex',gap:8}}>
            <button className="ledger-import-btn" onClick={() => setShowImport(true)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Import CSV
            </button>
            <button className="ledger-add-btn" onClick={() => setShowModal(true)}>+ Add Entry</button>
            <button className="ledger-bulk-del-btn" onClick={() => setShowBulkDelete(true)} title="Bulk delete by date range">🗑 Bulk Delete</button>
          </div>
        )}
      </div>

      {/* Top-level tabs */}
      <div className="ledger-main-tabs">
        {TAB_DEFS.map(t => (
          <button key={t.key} className={`ledger-main-tab${activeTab===t.key?' lmt-active':''}`}
            onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Bank Ledger tab ── */}
      {activeTab === 'bank' && (
        <>
          {/* Balance summary */}
          {balance && (
            <div className="ledger-summary">
              {/* Opening balance warning — if non-zero it was inflating the balance */}
              {parseFloat(balance.opening) > 0 && (
                <OpeningBalanceWarning
                  opening={balance.opening}
                  onZeroed={() => transactionsAPI.balance().then(b => setBalance(b))}
                />
              )}
              <div className="ls-card ls-credit">
                <div className="ls-label">Total Credits</div>
                <div className="ls-value">{fmt(balance.total_credits)}</div>
              </div>
              <div className="ls-card ls-debit">
                <div className="ls-label">Total Debits</div>
                <div className="ls-value">{fmt(balance.total_debits)}</div>
              </div>
              <div className={`ls-card ${parseFloat(balance.current_balance) >= 0 ? 'ls-balance-pos' : 'ls-balance-neg'}`}>
                <div className="ls-label">Net Balance</div>
                <div className="ls-value">{fmt(balance.current_balance)}</div>
              </div>
              {(typeFilter !== 'all' || catFilter !== 'all' || search) && (
                <>
                  <div className="ls-card ls-filtered">
                    <div className="ls-label">Filtered: Credits</div>
                    <div className="ls-value">{fmt(totalCredit)}</div>
                  </div>
                  <div className="ls-card ls-filtered-d">
                    <div className="ls-label">Filtered: Debits</div>
                    <div className="ls-value">{fmt(totalDebit)}</div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="ledger-filters">
            <div className="ledger-type-tabs">
              {['all','credit','debit'].map(t => (
                <button key={t} className={`ledger-type-tab${typeFilter===t?' active':''}`}
                  onClick={() => { setTypeFilter(t); setPage(1); }}>
                  {t === 'all' ? 'All Entries' : t === 'credit' ? '+ Credits' : '− Debits'}
                </button>
              ))}
            </div>
            <div className="ledger-filter-row">
              <select className="ledger-select" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
                <option value="all">All Categories</option>
                {allCats.map(c => <option key={c} value={c}>{CAT_ICONS[c] || '📋'} {c.replace(/_/g,' ')}</option>)}
              </select>
              <div className="ledger-search-wrap">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input className="ledger-search" value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search description, category…" />
              </div>
              <span className="ledger-count">{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</span>
            </div>
          </div>

          {/* Table */}
          <div className="ledger-table-wrap">
            {loading ? (
              <div className="ledger-loading">Loading transactions…</div>
            ) : paginated.length === 0 ? (
              <div className="ledger-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2">
                  <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
                <p>{transactions.length === 0 ? 'No transactions yet. Add a debit or credit entry.' : 'No entries match the current filter.'}</p>
              </div>
            ) : (
              <table className="ledger-table">
                <thead>
                  <tr>
                    <SortTh label="Date / Paid On" col="tx_date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                    <th>Type</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Reference</th>
                    <SortTh label="Amount" col="amount" sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="th-right" />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(tx => (
                    <tr key={tx.id}>
                      <td className="td-date">{fmtDate(tx.tx_date)}</td>
                      <td>
                        <span className={`ledger-type-badge ${tx.type}`}>
                          {tx.type === 'credit' ? '+ Credit' : '− Debit'}
                        </span>
                      </td>
                      <td className="td-cat">
                        <span className="cat-icon">{CAT_ICONS[tx.category] || '📋'}</span>
                        {(tx.category || 'other').replace(/_/g,' ')}
                      </td>
                      <td className="td-desc">{tx.description || '—'}</td>
                      <td className="td-ref">
                        {tx.invoice_number
                          ? <span className="ledger-ref">#{tx.invoice_number}</span>
                          : tx.reference || '—'
                        }
                      </td>
                      <td className={`td-amount ${tx.type}`}>
                        {tx.type === 'credit' ? '+' : '−'} {fmt(tx.amount, tx.currency)}
                      </td>
                      <td className="td-del">
                        <button className="ledger-edit-btn" onClick={() => setEditEntry(tx)} title="Edit entry">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        {tx.category !== 'invoice_payment' ? (
                          <button className="ledger-del-btn" onClick={() => handleDelete(tx.id, tx.category)} title="Delete">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
                              <path d="M9 6V4h6v2"/>
                            </svg>
                          </button>
                        ) : (
                          <span className="ledger-auto-badge" title="Auto-managed via invoice payment">auto</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ledger-pagination">
              <button className="pg-btn" disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
              <button className="pg-btn" disabled={safePage === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <div className="pg-info">
                Page <strong>{safePage}</strong> of <strong>{totalPages}</strong>
                <span className="pg-sub">({filtered.length} total)</span>
              </div>
              <button className="pg-btn" disabled={safePage === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
              <button className="pg-btn" disabled={safePage === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          )}
        </>
      )}

      {/* ── GST tab ── */}
      {activeTab === 'gst' && <GSTTab firm={firm} />}

      {/* ── TDS tab ── */}
      {activeTab === 'tds' && <TDSTab />}

      {showModal      && <AddEntryModal onClose={() => setShowModal(false)} onSaved={load} />}
      {showImport     && <BankImportModal onClose={() => setShowImport(false)} onSaved={load} />}
      {showBulkDelete && <BulkDeleteModal onClose={() => setShowBulkDelete(false)}
                           onDeleted={(n) => { alert(`✅ ${n} entries deleted.`); load(); }} />}
      {editEntry      && <EditEntryModal tx={editEntry} onClose={() => setEditEntry(null)} onSaved={load} />}
    </div>
  );
}
